import React, { useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { API } from '@/App';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Search, Calendar, MapPin, Phone, Mail, Building2, User, Syringe,
  FileText, Plus, AlertTriangle, History, Printer, Loader2,
} from 'lucide-react';
import { toast } from 'sonner';

const HistorialPage = () => {
  const navigate = useNavigate();
  const [rutInput, setRutInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [searched, setSearched] = useState(false);
  const inputRef = useRef(null);

  const handleSearch = useCallback(async (q) => {
    const query = (q ?? rutInput).trim();
    if (!query) {
      toast.error('Ingresa un RUT o pasaporte');
      return;
    }
    setLoading(true);
    setSearched(true);
    setData(null);
    try {
      const r = await axios.get(`${API}/pacientes/historial`, {
        params: { rut: query },
        withCredentials: true,
      });
      setData(r.data);
      if (!r.data.encontrado) {
        toast.info('No se encontraron registros para ese RUT');
      } else if (r.data.paciente?.rut) {
        setRutInput(r.data.paciente.rut);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al buscar');
    } finally {
      setLoading(false);
    }
  }, [rutInput]);

  const onKeyDown = (e) => {
    if (e.key === 'Enter') handleSearch();
  };

  const handleNuevaVacunacion = () => {
    if (!data?.paciente) return;
    // Navigate to new vaccination, passing RUT in query state for prefill
    const rut = data.paciente.rut || data.paciente.pasaporte || '';
    navigate(`/vaccinations/new?rut=${encodeURIComponent(rut)}`);
  };

  const _fetchPdfBlob = async () => {
    if (!data?.paciente_id) {
      toast.info('Este paciente solo existe en historial antiguo. Crea una vacunación o regístralo desde Pacientes para generar PDF.');
      return null;
    }
    try {
      const r = await axios.get(`${API}/reports/pdf/paciente/${data.paciente_id}`, {
        params: { tipo: data.origen === 'pacientes_empresa' ? 'empresa' : 'general' },
        withCredentials: true,
        responseType: 'blob',
      });
      return new Blob([r.data], { type: 'application/pdf' });
    } catch (e) {
      console.error('PDF fetch error:', e);
      toast.error('Error al generar PDF');
      return null;
    }
  };

  const handlePdfFicha = async () => {
    const blob = await _fetchPdfBlob();
    if (!blob) return;
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    const safe = (data.paciente.nombre_completo || 'paciente').replace(/[^A-Za-z0-9._-]+/g, '_');
    link.setAttribute('download', `ficha_${safe}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000);
    toast.success('PDF descargado');
  };

  const handlePrint = async () => {
    const blob = await _fetchPdfBlob();
    if (!blob) return;
    const blobUrl = window.URL.createObjectURL(blob);
    // Open PDF in new tab; user can print from the browser PDF viewer
    const printWindow = window.open(blobUrl, '_blank');
    if (!printWindow) {
      toast.error('Tu navegador bloqueó la nueva pestaña. Permítelas o usa Descargar.');
      window.URL.revokeObjectURL(blobUrl);
      return;
    }
    // Try to trigger print automatically once the PDF is loaded
    printWindow.addEventListener('load', () => {
      try { printWindow.print(); } catch (e) {
        console.warn('Auto-print failed, user can press Ctrl+P:', e);
      }
    });
    setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60000);
    toast.success('Abriendo vista de impresión');
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="historial-page">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900 flex items-center gap-2">
            <History className="w-6 h-6 text-blue-600" /> Historial por RUT
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Consulta la ficha y el historial de vacunaciones de cualquier paciente
          </p>
        </div>

        {/* Search bar */}
        <Card>
          <CardContent className="p-5">
            <div className="flex gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  ref={inputRef}
                  value={rutInput}
                  onChange={(e) => setRutInput(e.target.value)}
                  onKeyDown={onKeyDown}
                  placeholder="Ingresa RUT (ej: 12.345.678-9, 123456789, o pasaporte)"
                  className="pl-11 h-12 text-lg"
                  data-testid="input-rut-historial"
                  autoFocus
                />
              </div>
              <Button
                size="lg"
                onClick={() => handleSearch()}
                disabled={loading}
                data-testid="btn-buscar-historial"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Search className="w-5 h-5 mr-2" />}
                {loading ? 'Buscando...' : 'Buscar'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Results */}
        {searched && !loading && !data?.encontrado && (
          <Card>
            <CardContent className="p-10 text-center">
              <User className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <h3 className="text-lg font-medium text-slate-900">Paciente no encontrado</h3>
              <p className="text-sm text-slate-500 mt-1">
                Verifica el RUT o registra al paciente en el módulo Pacientes
              </p>
            </CardContent>
          </Card>
        )}

        {data?.encontrado && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="historial-results">
            {/* Patient card */}
            <Card className="lg:col-span-1">
              <CardContent className="p-6 space-y-4">
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-12 h-12 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-semibold text-lg">
                      {(data.paciente.nombre_completo || '?').split(' ').map(s => s[0]).slice(0, 2).join('').toUpperCase()}
                    </div>
                    <div>
                      <h2 className="text-lg font-semibold text-slate-900 leading-tight">
                        {data.paciente.nombre_completo || '—'}
                      </h2>
                      <p className="text-sm font-mono text-slate-500">
                        {data.paciente.rut || data.paciente.pasaporte || '—'}
                      </p>
                    </div>
                  </div>
                  {data.origen && (
                    <Badge variant="secondary" className="text-xs">
                      {data.origen === 'pacientes' && '📁 Base general'}
                      {data.origen === 'pacientes_empresa' && '🏢 Base empresa'}
                      {data.origen === 'vaccinations' && '🗂️ Historial antiguo'}
                    </Badge>
                  )}
                </div>

                <div className="space-y-2 pt-2 border-t border-slate-100">
                  <InfoRow icon={Calendar} label="Fecha nac." value={data.paciente.fecha_nacimiento} extra={data.paciente.edad ? `(${data.paciente.edad})` : ''} />
                  <InfoRow icon={MapPin} label="Comuna" value={data.paciente.comuna} />
                  <InfoRow icon={Phone} label="Teléfono" value={data.paciente.telefono} />
                  <InfoRow icon={Mail} label="Email" value={data.paciente.email} />
                  {data.paciente.empresa && <InfoRow icon={Building2} label="Empresa" value={data.paciente.empresa} />}
                </div>

                <div className="pt-3 border-t border-slate-100 space-y-2">
                  <Button className="w-full" onClick={handleNuevaVacunacion} data-testid="btn-nueva-vacunacion-historial">
                    <Plus className="w-4 h-4 mr-2" /> Nueva vacunación
                  </Button>
                  <div className="grid grid-cols-2 gap-2">
                    <Button variant="outline" onClick={handlePdfFicha} data-testid="btn-pdf-historial">
                      <FileText className="w-4 h-4 mr-2" /> PDF
                    </Button>
                    <Button variant="outline" onClick={handlePrint}>
                      <Printer className="w-4 h-4 mr-2" /> Imprimir
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Timeline + Alerts */}
            <div className="lg:col-span-2 space-y-6">
              {/* Alerts */}
              {data.alertas?.length > 0 && (
                <Card className="border-amber-200 bg-amber-50/40">
                  <CardContent className="p-5">
                    <h3 className="font-semibold text-amber-900 flex items-center gap-2 mb-3">
                      <AlertTriangle className="w-4 h-4" />
                      Alertas pendientes ({data.alertas.length})
                    </h3>
                    <ul className="space-y-2">
                      {data.alertas.map((a, i) => (
                        <li key={a.id || `alert-${i}`} className="flex justify-between items-start text-sm border-l-2 border-amber-400 pl-3">
                          <div>
                            <p className="font-medium text-slate-900">{a.tipo_vacuna || a.titulo || a.vacuna || 'Alerta'}</p>
                            <p className="text-slate-600 text-xs">{a.descripcion || a.motivo || ''}</p>
                          </div>
                          <span className="text-xs text-slate-500 font-mono whitespace-nowrap">{a.fecha_alerta || a.fecha_proxima || ''}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )}

              {/* Vaccinations timeline */}
              <Card>
                <CardContent className="p-5">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                      <Syringe className="w-4 h-4 text-blue-600" />
                      Historial de vacunaciones
                    </h3>
                    <Badge variant="secondary" data-testid="total-vacunaciones">
                      {data.total_vacunaciones} {data.total_vacunaciones === 1 ? 'registro' : 'registros'}
                    </Badge>
                  </div>

                  {data.vacunaciones.length === 0 ? (
                    <div className="text-center py-10 text-slate-500 text-sm">
                      <Syringe className="w-10 h-10 mx-auto text-slate-300 mb-2" />
                      Sin registros de vacunación
                    </div>
                  ) : (
                    <div className="relative">
                      <div className="absolute left-3 top-2 bottom-2 w-px bg-slate-200" />
                      <ul className="space-y-4">
                        {data.vacunaciones.map((v, i) => (
                          <li key={v.id || `vacc-${i}`} className="relative pl-9" data-testid={`vacc-row-${i}`}>
                            <div className="absolute left-0 top-1 w-7 h-7 rounded-full bg-blue-100 border-2 border-white shadow-sm flex items-center justify-center">
                              <Syringe className="w-3.5 h-3.5 text-blue-700" />
                            </div>
                            <div className="bg-slate-50 hover:bg-slate-100/70 rounded-md px-4 py-3 transition-colors">
                              <div className="flex justify-between items-start gap-3">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <h4 className="font-medium text-slate-900">
                                      {v.vacuna || (v.vacunas?.map(x => x.vacuna).join(', ')) || '—'}
                                    </h4>
                                    {v.dosis && <Badge variant="outline" className="text-xs">{v.dosis}</Badge>}
                                    {v.tipo_vacunacion && <Badge variant="secondary" className="text-xs">{v.tipo_vacunacion}</Badge>}
                                  </div>
                                  <div className="text-xs text-slate-500 mt-1 flex flex-wrap gap-x-3">
                                    {v.lote_serie && <span>Lote: <span className="font-mono">{v.lote_serie}</span></span>}
                                    {v.administracion && <span>Por: {v.administracion}</span>}
                                    {v.empresa && <span>· {v.empresa}</span>}
                                  </div>
                                </div>
                                <div className="text-right shrink-0">
                                  <p className="text-sm font-mono text-slate-700">{v.fecha_vacunacion || '—'}</p>
                                  {v.monto > 0 && <p className="text-xs text-emerald-700 font-semibold mt-1">${v.monto?.toLocaleString('es-CL')}</p>}
                                </div>
                              </div>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Empty state when nothing searched yet */}
        {!searched && !loading && (
          <Card className="border-dashed">
            <CardContent className="p-12 text-center">
              <History className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <h3 className="text-lg font-medium text-slate-900">Buscar historial</h3>
              <p className="text-sm text-slate-500 mt-1">
                Escribe un RUT en cualquier formato y presiona Buscar
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
};

const InfoRow = ({ icon: Icon, label, value, extra }) => (
  <div className="flex items-start gap-2 text-sm">
    <Icon className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
    <div className="flex-1">
      <span className="text-slate-500">{label}: </span>
      <span className="text-slate-900">{value || '—'}</span>
      {extra && <span className="text-slate-500 ml-1">{extra}</span>}
    </div>
  </div>
);

export default HistorialPage;
